// src/pages/roles/CreateRolePage.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Components
import RoleForm from '../components/RoleForm';

// Hooks
import { useRoles } from '../hooks/useRoles';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { getCreatedId } from '../../../utils/helpers/getCreatedId';
import { FormPage, FormActions } from '../../../components/common/form';

export default function CreateRolePage() {
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { t } = useTranslation(['roles', 'common']);
  const { showSuccess, showError } = useSnackbar();

  // State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // API hooks
  const {
    createRole,
    loading,
    error,
    clearError,
  } = useRoles();

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('roles:validation.name_required');
    } else if (formData.name.length > 100) {
      errors.name = t('roles:validation.name_max_length');
    }

    if (!formData.description.trim()) {
      errors.description = t('roles:validation.description_required');
    } else if (formData.description.length > 500) {
      errors.description = t('roles:validation.description_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await createRole(formData);

      if (result.success) {
        showSuccess(t('roles:success.role_created'));
        // Open the new record (replace, so Back skips the filled-in form)
        const newId = getCreatedId(result, 'role_id');
        navigate(newId ? `/roles/${newId}` : '/roles', { replace: true });
      } else {
        showError(result.error || t('roles:errors.create_failed'));
      }
    } catch (error) {
      showError(t('roles:errors.unexpected_error'));
    }
  }, [validateForm, formData, createRole, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack('/roles');
  }, [navigateBack]);

  // The form's own info note covers what happens next (permissions, staff)
  return (
    <FormPage
      title={t('roles:form.create_title')}
      subtitle={t('roles:form.create_subtitle')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
    >
      <RoleForm
        formData={formData}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
        loading={loading}
        mode="create"
      />
      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={loading ? t('roles:actions.creating') : t('roles:actions.create')}
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
